import { Button } from "@/components/button";
import { Calendar } from "@/components/calendar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/dialog";
import { Editor } from "@/components/editor";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/popover";
import { useSetExperimentRolloutDurability } from "@/http/api-hooks";
import { Experiment, ExperimentRolloutDurability } from "@/http/api-types";
import { cn, getErrorMessage } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon, CheckCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { parseTimeFromDate } from "./ExperimentForm";
import { combineDateAndTime, toUTCCalendarDate } from "./experiment-form-helpers";
import {
  durabilityRecordsEndDate,
  durabilityRequiresNote,
  EXPERIMENT_ROLLOUT_DURABILITY_OPTIONS,
  getExperimentRolloutDurabilityOption,
} from "./experiment-rollout";

type RolloutDurabilityDialogProps = {
  experiment: Experiment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // The mutation hook already refreshes the list and detail caches, so this is only for
  // callers that keep their own copy of the experiment, like the Summary tab's form.
  onSaved?: (experiment: Experiment) => void;
};

// The full durability form, shared by the Summary tab and the experiments list.
//
// It lives in one component because reverted and churned need a real explanation: the
// note is required, and it is written with the rich Editor so it can link the revert PR,
// the incident, or the client comms. The list used to offer a plain textarea for the same
// field, which quietly produced a worse record of the case that matters most.
export function RolloutDurabilityDialog({ experiment, open, onOpenChange, onSaved }: RolloutDurabilityDialogProps) {
  const [selectedDurability, setSelectedDurability] = useState<ExperimentRolloutDurability | "">(
    experiment.rollout_durability_status || "",
  );
  const [durabilityDate, setDurabilityDate] = useState<Date | null>(
    toUTCCalendarDate(experiment.rollout_durability_ended_at),
  );
  const [durabilityTime, setDurabilityTime] = useState(
    experiment.rollout_durability_ended_at
      ? parseTimeFromDate(new Date(experiment.rollout_durability_ended_at))
      : "00:00",
  );
  const [durabilityPopoverOpen, setDurabilityPopoverOpen] = useState(false);
  const [durabilityNoteDraft, setDurabilityNoteDraft] = useState(experiment.rollout_durability_note || "");
  const { trigger: setExperimentRolloutDurability, isMutating: isSettingDurability } =
    useSetExperimentRolloutDurability(experiment.id);

  // Re-seed every time it opens, so a cancelled edit never leaks into the next one and a
  // change made elsewhere (the list's one-click confirm) is picked up.
  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedDurability(experiment.rollout_durability_status || "");
    setDurabilityDate(toUTCCalendarDate(experiment.rollout_durability_ended_at));
    setDurabilityTime(
      experiment.rollout_durability_ended_at
        ? parseTimeFromDate(new Date(experiment.rollout_durability_ended_at))
        : "00:00",
    );
    setDurabilityPopoverOpen(false);
    setDurabilityNoteDraft(experiment.rollout_durability_note || "");
  }, [
    open,
    experiment.rollout_durability_status,
    experiment.rollout_durability_ended_at,
    experiment.rollout_durability_note,
  ]);

  const durabilityNoteMissing = durabilityRequiresNote(selectedDurability) && durabilityNoteDraft.trim().length === 0;
  const selectedDurabilityOption = getExperimentRolloutDurabilityOption(selectedDurability || undefined);
  const durabilityNeedsEndDate = durabilityRecordsEndDate(selectedDurability);

  const handleDurabilityDialogOpenChange = (nextOpen: boolean) => {
    if (isSettingDurability) {
      return;
    }
    onOpenChange(nextOpen);
  };

  const handleSetDurability = async () => {
    if (!selectedDurability) {
      toast.error("Please select a rollout durability");
      return;
    }
    // Only a revert or a churn ended anything, so only those ask for a date. Still live
    // sends none and lets the server stamp the write instant.
    const needsEndDate = durabilityRecordsEndDate(selectedDurability);
    if (needsEndDate && !durabilityDate) {
      toast.error(`Please select the date this ${selectedDurabilityOption?.label.toLowerCase()} happened`);
      return;
    }
    const note = durabilityNoteDraft.trim();
    if (durabilityRequiresNote(selectedDurability) && !note) {
      toast.error("Please add a note explaining what happened");
      return;
    }

    try {
      const endedAt =
        needsEndDate && durabilityDate ? combineDateAndTime(durabilityDate, durabilityTime, false) : undefined;
      const updatedExperiment = await setExperimentRolloutDurability({
        rollout_durability_status: selectedDurability,
        rollout_durability_ended_at: endedAt?.toISOString(),
        rollout_durability_note: note || undefined,
      });
      setDurabilityNoteDraft(note);
      onOpenChange(false);
      toast.success("Rollout durability updated successfully");
      onSaved?.(updatedExperiment);
    } catch (error) {
      toast.error("Failed to set rollout durability", { description: getErrorMessage(error) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDurabilityDialogOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Rollout Durability</DialogTitle>
          <DialogDescription>
            Record what happened to this rollout after it shipped. The date is when it happened, not when you are
            recording it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            {EXPERIMENT_ROLLOUT_DURABILITY_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setSelectedDurability(option.value)}
                className={cn(
                  "w-full rounded-md border p-3 text-left transition-colors",
                  selectedDurability === option.value
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50",
                )}
              >
                <p className="text-sm font-medium">{option.label}</p>
                <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
              </button>
            ))}
          </div>

          {/* Still live gets no date field at all: nothing ended, so there is no moment
            to ask for, and the server stamps the write instant. Asking would only
            invite a backdated "still live" claim that the freshness badge cannot see. */}
          {durabilityNeedsEndDate && (
            <div>
              <Label className="text-xs text-muted-foreground">{selectedDurabilityOption?.endedAtLabel}</Label>
              <div className="mt-1 flex gap-2">
                <Popover open={durabilityPopoverOpen} onOpenChange={setDurabilityPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "flex-1 justify-start text-left font-normal",
                        !durabilityDate && "text-muted-foreground",
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {durabilityDate ? format(durabilityDate, "LLL dd, y") : <span>Pick date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={durabilityDate || undefined}
                      onDayClick={day => {
                        setDurabilityDate(day);
                        setDurabilityPopoverOpen(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <div className="relative">
                  <Input
                    type="time"
                    value={durabilityTime}
                    onChange={event => setDurabilityTime(event.target.value)}
                    className="w-32 pr-12"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                    UTC
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>
              Note
              {durabilityRequiresNote(selectedDurability) ? (
                <span className="ml-1 text-destructive">*</span>
              ) : (
                <span className="ml-1 text-xs font-normal text-muted-foreground">(optional)</span>
              )}
            </Label>
            <Editor
              value={durabilityNoteDraft}
              onChange={setDurabilityNoteDraft}
              placeholder={
                durabilityRequiresNote(selectedDurability)
                  ? "Why did this happen? Link the revert PR, incident, or client comms."
                  : "Anything worth noting about this check."
              }
              experimentId={experiment.id?.toString()}
              editorClassName="min-h-[140px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleDurabilityDialogOpenChange(false)}
            disabled={isSettingDurability}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSetDurability}
            disabled={
              isSettingDurability ||
              !selectedDurability ||
              (durabilityNeedsEndDate && !durabilityDate) ||
              durabilityNoteMissing
            }
          >
            {!isSettingDurability && <CheckCircle className="h-4 w-4 mr-2" />}
            {isSettingDurability ? "Saving..." : "Save Durability"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
